import * as React from 'react';
import * as Yup from 'yup';
import { Field, Formik, FieldProps, FormikProps, FormikValues } from 'formik';
import { MutationFn } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import { RouteComponentProps, withRouter } from 'react-router';
import * as moment from 'moment';
import {
  StyledForm,
  FieldRow,
  StyledFormGroup,
  StyledFormSelect,
  StyledDatePicker,
  Card,
  // HeroImage,
  ConfirmationModal,
} from '../../components';
import Span from '../../typography/Span';
import { formatFormValues, getLoggedInUser } from '../../utils/helpers';
import { formatStakeholderGqlError } from './stakeholderError';
import Footer from '../../containers/events/Footer';
import { StatusValues } from '../../components/Badges';
import { ImageObject, SelectObject } from '../../utils/types';
import {
  UpdateStakeholderMutationFn,
  StakeholderStatus,
  UpdateStakeholderComponent,
  GetAllStakeholdersDocument,
  UserRole,
} from '../../generated/graphql';
import { SteynCityContext } from '../../components/ContextProvider';
import { STAKEHOLDER_ROLE as StakeHolders } from '../../utils/enums';

// @ts-ignore
const QRCode = require('qrcode.react');

const STAKEHOLDER_ROLE: SelectObject[] = StakeHolders;

const STATUS_OPTIONS: SelectObject[] = [
  { label: 'New', value: 'NEW' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Archived', value: 'ARCHIVED' },
];

// const downloadQR = (initialValues: any) => {
//   const canvas = document.getElementById('123456');
//   const { firstName, lastName } = initialValues;
//   if (canvas) {
//     // @ts-ignore
//     const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
//     const downloadLink = document.createElement('a');
//     downloadLink.href = pngUrl;
//     downloadLink.download = `${firstName} ${lastName}.png`;
//     document.body.appendChild(downloadLink);
//     downloadLink.click();
//     document.body.removeChild(downloadLink);
//   }
// };

const PHONE_FORMAT_MESSAGE = 'Incorrect format. Please use +2711...';

const isValidSaPhoneNumber = (value: string): boolean => {
  if (!value.startsWith('+27')) {
    return false;
  }
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 11;
};

const STAKEHOLDER_MUTATION_FIELDS = [
  'id',
  'firstName',
  'lastName',
  'dob',
  'country',
  'homeNumber',
  'cellNumber',
  'email',
  'idNumber',
  'residentialAddress',
  'postalAddress',
  'unitNumber',
  'status',
  'role',
];

const CreateStakeholderValidation = Yup.object().shape({
  cellNumber: Yup.string()
    .required('Required')
    .test('phoneFormat', PHONE_FORMAT_MESSAGE, (value) => {
      if (!value) {
        return false;
      }
      return isValidSaPhoneNumber(value);
    }),
  country: Yup.string().required('Required'),
  dob: Yup.mixed()
    .nullable(true)
    .required('Required')
    .test('olderThanEighteen', 'Should be older than 18', function (value: any) {
      if (!value) {
        return true;
      }
      const { role } = this.parent;
      if (role && role.value === 'OWNER' && (moment as any)().diff((moment as any)(value), 'years') < 18) {
        return false;
      }
      return true;
    }),
  email: Yup.string().email().required('Required'),
  firstName: Yup.string().required('Required'),
  homeNumber: Yup.string()
    .nullable(true)
    .test('phoneFormat', PHONE_FORMAT_MESSAGE, (value) => {
      if (value == null || value === '') {
        return true;
      }
      return isValidSaPhoneNumber(value);
    }),
  idNumber: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  postalAddress: Yup.string().nullable(true),
  unitNumber: Yup.string().nullable(true),
  residentialAddress: Yup.string().required('Required'),
  role: Yup.mixed().required('Required'),
  status: Yup.mixed().required('Required'),
});

type StakeholderFormValues = {
  cellNumber?: string;
  country?: string;
  dob?: Date;
  email?: string;
  firstName?: string;
  homeNumber?: string;
  idNumber?: string;
  lastName?: string;
  postalAddress?: string;
  unitNumber?: string;
  residentialAddress?: string;
  type?: SelectObject;
  status?: SelectObject;
  uniqueID?: string;
  role?: SelectObject;
};

type Props = RouteComponentProps & {
  mutation: MutationFn;
  error?: ApolloError;
  buttonText?: string;
  loading: boolean;
  initialValues?: FormikValues;
  image?: ImageObject;
};

const StakeholderDetailsForm: React.FC<Props> = ({ initialValues, image, error, mutation, loading, history }) => {
  const context = React.useContext(SteynCityContext);
  const [currentLoading, setCurrentLoading] = React.useState('');
  const [confirmationModal, setConfirmationModal] = React.useState(false);
  const user = getLoggedInUser();

  function handleApprove(updateStakeholder: UpdateStakeholderMutationFn, id: string) {
    updateStakeholder({
      variables: { id, status: StakeholderStatus.New },
    });
  }

  function handleSuccessfulApprove() {
    context.addToast('Stakeholder approved successfully!', 'SUCCESS');
    history.push('/stakeholders/all');
  }

  const formatPhoneNumber = (phoneNumber: string): string => {
    let formattedNumber = phoneNumber.replace(/\s+/g, '');

    // Strip any non-digit characters except the leading +
    formattedNumber = formattedNumber.replace(/(?!^\+)[^\d]/g, '');

    if (formattedNumber.startsWith('0')) {
      formattedNumber = '+27' + formattedNumber.substring(1);
    } else if (!formattedNumber.startsWith('+')) {
      if (/^\d{9}$/.test(formattedNumber)) {
        formattedNumber = '+27' + formattedNumber;
      } else if (/^\d{10}$/.test(formattedNumber) && !formattedNumber.startsWith('0')) {
        formattedNumber = '+27' + formattedNumber;
      } else {
        formattedNumber = '+27' + formattedNumber;
      }
    }

    return formattedNumber;
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={CreateStakeholderValidation}
      initialValues={
        initialValues || {
          cellNumber: '',
          country: '',
          email: '',
          firstName: '',
          homeNumber: '',
          idNumber: '',
          lastName: '',
          postalAddress: '',
          residentialAddress: '',
          unitNumber: '',
          status: STATUS_OPTIONS[1],
          role: STAKEHOLDER_ROLE.find((role) => role.value === 'OWNER'),
          dob: null,
        }
      }
      onSubmit={(values: StakeholderFormValues) => {
        setCurrentLoading('SAVE');
        const formattedValues = formatFormValues(values) as Record<string, unknown>;
        const mutationVariables = STAKEHOLDER_MUTATION_FIELDS.reduce<Record<string, unknown>>((acc, field) => {
          if (formattedValues[field] !== undefined) {
            acc[field] = formattedValues[field];
          }
          return acc;
        }, {});
        mutation({ variables: mutationVariables }).catch(() => {
          setCurrentLoading('');
        });
      }}
      render={(formikProps: FormikProps<StakeholderFormValues>) => {
        return (
          <StyledForm flexDirection="row" flexWrap="nowrap" pb={6}>
            <Footer
              previousButton={false}
              nextButton={false}
              archiveButton={false}
              approveButton={!!(initialValues && initialValues.status.value === StatusValues.Preapproved)}
              saveText="Save & Update"
              isSubmit={false}
              onClickSave={() => formikProps.submitForm()}
              onClickApprove={() => setConfirmationModal(true)}
              currentlyLoading={currentLoading}
              loading={loading}
            />
            <Card flex={3} bg="white" p={4}>
              <FieldRow>
                <Field
                  name="firstName"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} mr={[0, 0, 4]} field={field} form={formikProps} label="First Name" />
                  )}
                />
                <Field
                  name="lastName"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} field={field} mr={[0, 0, 4]} form={formikProps} label="Last Name" />
                  )}
                />
                <Field
                  name="dob"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledDatePicker label="Date of Birth" {...field} field={field} form={formikProps} />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="country"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} mr={[0, 0, 4]} field={field} form={formikProps} label="Country" />
                  )}
                />
                <Field
                  name="homeNumber"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup
                      {...field}
                      mr={[0, 0, 4]}
                      field={field}
                      form={formikProps}
                      label="Home Number"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        if (value) {
                          const digitsOnly = value.replace(/\D/g, '');
                          if (digitsOnly.length >= 9) {
                            const formattedNumber = formatPhoneNumber(value);
                            formikProps.setFieldValue('homeNumber', formattedNumber);
                          } else {
                            field.onChange(e);
                          }
                        } else {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
                <Field
                  name="cellNumber"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup
                      {...field}
                      field={field}
                      form={formikProps}
                      label="Cell Number"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        // Format the phone number when it changes
                        const value = e.target.value;
                        if (value) {
                          // Format when the user has finished typing (at least 9 chars for SA numbers)
                          const digitsOnly = value.replace(/\D/g, '');
                          if (digitsOnly.length >= 9) {
                            const formattedNumber = formatPhoneNumber(value);
                            formikProps.setFieldValue('cellNumber', formattedNumber);
                          } else {
                            // Otherwise just set the raw value
                            field.onChange(e);
                          }
                        } else {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="email"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} mr={[0, 0, 4]} field={field} form={formikProps} label="Email" />
                  )}
                />
                <Field
                  name="idNumber"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="ID Number" />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="residentialAddress"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="Residential Address" />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="postalAddress"
                  render={({ field }: FieldProps<StakeholderFormValues>) => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="Postal Address" />
                  )}
                />
              </FieldRow>
            </Card>
            <Card ml={[0, 0, 4]} flex={1} bg="white" p={4}>
              {/* <HeroImage editable={false} src={image && image.scaled} id={initialValues && initialValues.id} /> */}
              <Field
                name="status"
                render={({ field }: FieldProps<StakeholderFormValues>) => (
                  <StyledFormSelect
                    {...field}
                    initialValue={STATUS_OPTIONS[1]}
                    field={field}
                    label="Status"
                    form={formikProps}
                    options={STATUS_OPTIONS}
                    disabled={
                      !initialValues ||
                      !initialValues.id ||
                      (user &&
                        ![UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin].some(
                          (role) => {
                            const userRole = user.role as string;
                            return role === userRole || role.toString() === userRole || role.toLowerCase() === userRole.toLowerCase();
                          }
                        ))
                    }
                  />
                )}
              />
              <Field
                name="id"
                render={({ field }: FieldProps<StakeholderFormValues>) => (
                  <StyledFormGroup
                    {...field}
                    field={field}
                    form={formikProps}
                    label="Unique ID"
                    placeholder="Auto generated unique ID"
                    disabled={true}
                  />
                )}
              />
              {/* {stakeholderID !== '' && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute' }}>
                    <QRCode
                      id="123456"
                      value={`steyncity://LostAndFoundScreen?id=${stakeholderID}`}
                      size={290}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <Button bg="putty" type="button" text="Download QR Code" onClick={() => downloadQR(initialValues)} />
                </div>
              )} */}
              <Field
                name="role"
                render={({ field }: FieldProps<StakeholderFormValues>) => (
                  <StyledFormSelect
                    {...field}
                    field={field}
                    label="Stakeholder Role"
                    form={formikProps}
                    options={STAKEHOLDER_ROLE}
                    disabled={
                      user &&
                      ![UserRole.Admin, UserRole.Superadmin, UserRole.StakeholderAdmin].some(
                        (role) => {
                          const userRole = user.role as string;
                          return role === userRole || role.toString() === userRole || role.toLowerCase() === userRole.toLowerCase();
                        }
                      )
                    }
                  />
                )}
              />
              <Field
                name="vmsTag"
                render={({ field }: FieldProps<StakeholderFormValues>) => (
                  <StyledFormGroup {...field} field={field} form={formikProps} label="Stakeholder Tag" />
                )}
              />

              {error && (
                <Span width="100%" textAlign="center" color="error" mb={3}>
                  {formatStakeholderGqlError(error)}
                </Span>
              )}
            </Card>
            <UpdateStakeholderComponent
              onCompleted={() => handleSuccessfulApprove()}
              awaitRefetchQueries={true}
              refetchQueries={() => [{ query: GetAllStakeholdersDocument }]}
            >
              {(updateStakeholder, { loading: approveLoading }) => {
                return (
                  <ConfirmationModal
                    heading="Approve Stakeholder"
                    open={confirmationModal}
                    onClose={() => setConfirmationModal(false)}
                    loading={approveLoading}
                    onClickBack={() => setConfirmationModal(false)}
                    message="Are you sure you would like to approve this stakeholder?"
                    onClickConfirm={() => handleApprove(updateStakeholder, initialValues && initialValues.id)}
                  />
                );
              }}
            </UpdateStakeholderComponent>
          </StyledForm>
        );
      }}
    />
  );
};

export default withRouter(StakeholderDetailsForm);



// WEBPACK FOOTER //
// ./src/forms/stakeholders/StakeholderDetailsForm.tsx